import {
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Typography
} from "antd";
import dayjs from 'dayjs';
import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
const { Title } = Typography;

const PatientInformation = ({ getChildData, patientDetailsData }) => {
  const { t, i18n } = useTranslation();
  const dateFormat = "MM/DD/YYYY";
  const onChangeMethod = (e) => {
    callparent(e.target.value, "IsPatient");
  };
  const validatePhone = useCallback((_, value) => {
    // Example regex for phone number validation (adjust as needed)
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    if (!value || phoneRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(t("common:please_enter_a_valid_phone_number")));
  }, [t]);
  const callparent = (e, name) => {
    getChildData(e, name);
  };

  const disableFutureDates = (current) => {
    // Can not select days after today
    return current && current > dayjs().endOf('day');
  };

  // Memoize validation rules so they update when language changes
  const validationRules = useMemo(() => ({
    required: [{ required: true, message: t("patientDetails:please_choose_any_option") }],
    requiredSelect: [{ required: true, message: t("patientDetails:please_select_an_option") }],
    requiredField: [{ required: true, message: t("patientDetails:please_enter_this_field") }],
    firstName: [
      { required: true, message: t("patientDetails:please_enter_first_name"), whitespace: true },
      { max: 50, message: t("patientDetails:first_name_max_50_characters") }
    ],
    lastName: [
      { required: true, message: t("patientDetails:please_enter_last_name"), whitespace: true },
      { max: 50, message: t("patientDetails:last_name_max_50_characters") }
    ],
    middleName: [
      { max: 50, message: t("patientDetails:middle_name_max_50_characters") }
    ],
    dob: [
      { required: true, message: t("patientDetails:please_select_date_of_birth") }
    ],
    email: [
      { required: true, message: t("patientDetails:please_enter_email") },
      { type: "email", message: t("patientDetails:please_enter_valid_email") },
      { max: 100, message: t("patientDetails:email_max_100_characters") }
    ],
    phone: [
      { required: true, message: t("patientDetails:please_enter_phone_number") },
      { validator: validatePhone },
      { max: 20, message: t("patientDetails:phone_max_20_characters") }
    ],
    address: [
      { required: true, message: t("patientDetails:please_enter_address"), whitespace: true },
      { max: 100, message: t("patientDetails:address_max_100_characters") }
    ],
    address2: [
      { max: 100, message: t("patientDetails:address_max_100_characters") }
    ],
    city: [
      { required: true, message: t("patientDetails:please_enter_city_name"), whitespace: true },
      { max: 50, message: t("patientDetails:city_max_50_characters") }
    ],
    state: [
      { required: true, message: t("patientDetails:please_enter_state_name"), whitespace: true },
      { max: 50, message: t("patientDetails:state_max_50_characters") }
    ],
    zipCode: [
      { required: true, message: t("patientDetails:please_enter_zip_code") },
      { pattern: /^[0-9]+$/, message: t("patientDetails:zip_code_must_be_numeric") },
      { min: 5, message: t("patientDetails:zip_code_min_5_digits") },
      { max: 10, message: t("patientDetails:zip_code_max_10_digits") }
    ]
  }), [t, validatePhone]); // Re-create rules when language changes

  return (
    <Card
      styles={{ body: { padding: 0 }, header: { marginBottom: 20 } }}
      bordered={false}
      size="0"
      title={
        <Title
          className="font-semibold"
          style={{ marginBottom: "0" }}
          level={4}
        >
          {t("patientDetails:patient_information")}
        </Title>
      }
    >
      <Row gutter={[0, 0]} align={"bottom"} className="px-1 lg:px-6">
        <Col>
          <Form.Item
            name="areupatient"
            label={t("patientDetails:are_you_the_patient")}
            rules={validationRules.required}
          >

            <Radio.Group onChange={(e) => onChangeMethod(e)} value={patientDetailsData.methodOfRelease} >
              <Space direction="vertical" className="ms-4"
              >
                <Radio value={"yes"} className="font-normal">
                  {t("patientDetails:yes")}
                </Radio>
                <Radio value={"no"} className="font-normal">
                  {t("patientDetails:no")}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>
        {patientDetailsData.IsPatient == "no" ? (
          <Col>
            <Form.Item
              labelCol={{
                xl: 13,
              }}
              layout="horizontal"
              className="patient-form-item"
              name="associatedpatient"
              label={<label>{t("patientDetails:how_are_you_associated_with_the_patient")}</label>}
              rules={validationRules.requiredSelect}
            >
              <Select
                placeholder={t("patientDetails:select_an_option")}
                size="large"
                className="!w-full md:!w-[250px]"
                // style={{
                //   width: 250,
                // }}
                name="AssociatedWithPatient"
                onChange={(e) => callparent(e, "AssociatedWithPatient")}
                options={[
                  {
                    value: "1",
                    label: t("patientDetails:parent_legal_guardian"),
                  },
                  {
                    value: "2",
                    label: t("patientDetails:authorized_representative"),
                  },
                ]}
              />
            </Form.Item>
          </Col>
        ) : null}
      </Row>
      {patientDetailsData.IsPatient == "yes" || patientDetailsData.IsPatient == "no" ? (
        <Row gutter={[20, 0]} className="px-1 md:px-6">
          <Col xs={24} md={12}>
            <Form.Item
              label={t("patientDetails:patient_first_name")}
              name="firstname"
              onChange={(e) => callparent(e.target.value, "PFirstName")}
              required
              rules={validationRules.firstName}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("patientDetails:patient_last_name")}
              name="lastname"
              onChange={(e) => callparent(e.target.value, "PLastName")}
              required
              rules={validationRules.lastName}

            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xl={patientDetailsData.IsPatient == "no" && patientDetailsData.AssociatedWithPatient == 1 ? 6 : 8} xs={24} md={12}>
            <Form.Item
              name="dob"
              label={t("patientDetails:date_of_birth")}
              required
              rules={validationRules.dob}
            >
              <DatePicker
                format={dateFormat}
                disabledDate={disableFutureDates}
                style={{ minWidth: "100%" }}
                name="PDOB"
                onChange={(e) => callparent(e, "PDOB")}
                placeholder={t("common:select_date")}
              />
            </Form.Item>
          </Col>
          {patientDetailsData.IsPatient == "no" && patientDetailsData.AssociatedWithPatient == 1 ?
            <Col xl={patientDetailsData.IsPatient == "no" && patientDetailsData.AssociatedWithPatient == 1 ? 4 : 8} xs={24} md={12}>
              <Form.Item
                name="IsPatientMinor"
                label={t("patientDetails:is_the_patient_a_minor")}
                rules={validationRules.required}
              >

                <Radio.Group onChange={(e) => callparent(e.target.value, "IsPatientMinor")} value={patientDetailsData.IsPatientMinor}>
                  <Radio value={"yes"} className="font-normal">
                    {t("patientDetails:yes")}
                  </Radio>
                  <Radio value={"no"} className="font-normal">
                    {t("patientDetails:no")}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            : null}
          <Col xl={patientDetailsData.IsPatient == "no" && patientDetailsData.AssociatedWithPatient == 1 ? 8 : 8} xs={24} md={12}>
            <Form.Item
              label={t("patientDetails:email")}
              name="email"
              onChange={(e) => callparent(e.target.value, "PEmail")}
              required
              rules={validationRules.email}
            >
              <Input maxLength={100} />
            </Form.Item>
          </Col>

          <Col xl={patientDetailsData.IsPatient == "no" && patientDetailsData.AssociatedWithPatient == 1 ? 6 : 8} xs={24} md={12}>
            <Form.Item
              className="font-medium"
              label={t("patientDetails:phone_number")}
              required
              name="phone"
              onChange={(e) =>
                callparent(e.target.value, "PatientPhoneNumber")
              }
              rules={validationRules.phone}
            >
              <Input maxLength={20} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("patientDetails:address_line_1")}
              name="address1"
              onChange={(e) => callparent(e.target.value, "PAddLine1")}
              rules={validationRules.address}
            >
              <Input maxLength={150} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("patientDetails:address_line_2")}
              name="address2"
              onChange={(e) => callparent(e.target.value, "PAddLine2")}
            >
              <Input maxLength={150} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("patientDetails:city")}
              name="city"
              onChange={(e) => callparent(e.target.value, "PCity")}
              required
              rules={validationRules.city}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("patientDetails:state")}
              name="state"
              onChange={(e) => callparent(e.target.value, "PState")}
              required
              rules={validationRules.state}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("patientDetails:zip_code")}
              name="zipcode"
              onChange={(e) => callparent(e.target.value, "PZipCode")}
              required
              rules={validationRules.zipCode}
            >
              <Input maxLength={8} />
            </Form.Item>
          </Col>
        </Row>
      ) : null}
    </Card>
  );
};

export default PatientInformation;
